import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { c, font } from "../lib/theme";

// Promise-based confirmation. Wrap the app in <ConfirmProvider>, then call
// `await confirm({...})` from any handler before a destructive action:
//   if (!(await confirm({ title: "Delete?", message: "…", danger: true }))) return;
// This is the standard guard for every delete/irreversible action in the app.
export interface ConfirmOptions {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

type Confirm = (opts: ConfirmOptions) => Promise<boolean>;
const Ctx = createContext<Confirm | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<(v: boolean) => void>(() => {});

  const confirm = useCallback<Confirm>((o) => {
    setOpts(o);
    return new Promise<boolean>((resolve) => { resolver.current = resolve; });
  }, []);

  const close = useCallback((v: boolean) => {
    resolver.current(v);
    resolver.current = () => {};
    setOpts(null);
  }, []);

  // Esc cancels, Enter confirms while the dialog is open.
  useEffect(() => {
    if (!opts) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") { e.preventDefault(); close(false); }
      if (e.key === "Enter") { e.preventDefault(); close(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [opts, close]);

  return (
    <Ctx.Provider value={confirm}>
      {children}
      {opts && (
        <div
          onClick={() => close(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(3,5,11,0.72)", display: "grid", placeItems: "center", zIndex: 100 }}
        >
          <div
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{ width: 410, maxWidth: "90vw", background: c.panel, border: `1px solid ${c.line2}`, borderRadius: 8, padding: 22, boxShadow: "0 24px 64px rgba(0,0,0,0.55)" }}
          >
            <div style={{ font: `700 18px ${font.display}`, color: c.textHi, marginBottom: 8 }}>{opts.title}</div>
            <div style={{ fontSize: 15, color: c.muted2, lineHeight: 1.6, marginBottom: 20 }}>{opts.message}</div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 9 }}>
              <button
                onClick={() => close(false)}
                style={{ font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: c.muted2, background: c.navItem, border: `1px solid ${c.line2}`, padding: "9px 16px", borderRadius: 7, cursor: "pointer" }}
              >
                {opts.cancelLabel ?? "CANCEL"}
              </button>
              <button
                onClick={() => close(true)}
                style={{ font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: c.onAccent, background: opts.danger ? c.red : c.blue, border: "none", padding: "9px 16px", borderRadius: 7, cursor: "pointer" }}
              >
                {opts.confirmLabel ?? "CONFIRM"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}

export function useConfirm(): Confirm {
  const v = useContext(Ctx);
  if (!v) throw new Error("useConfirm outside ConfirmProvider");
  return v;
}
